import { useEffect, useState, useCallback, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useStable } from "../context/StableContext";
import api, { errText, resolveImage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import GestoreLegal from "../components/GestoreLegal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Trash2, Loader2, Search, X, Check, ChevronUp, ChevronDown, Star, Pencil, Archive, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = { admin: "Gestore", manager: "Gestore", staff: "Staff", owner: "Proprietario cavallo", readonly: "Lettura" };

const GROUPS = [
  { key: "gestori", label: "Gestori", roles: ["admin", "manager"] },
  { key: "staff", label: "Staff", roles: ["staff"] },
  { key: "owner", label: "Proprietari cavallo", roles: ["owner"] },
  { key: "readonly", label: "Lettura", roles: ["readonly"] },
];

export default function Settings() {
  const { current, perms, refresh } = useStable();

  if (perms.isOwner) return <Navigate to="/" replace />;

  return (
    <div className="space-y-6 eq-fade-up max-w-4xl">
      <div>
        <p className="font-mono-label text-[#8C532B] text-xs">Configurazione</p>
        <h1 className="font-display text-3xl font-bold text-[#1B3B2B]">Impostazioni</h1>
      </div>

      {(perms.canManage || perms.assist) && <LegacyResume current={current} assist={perms.assist} />}
      {(perms.canManage || perms.assist) && <LegacyExtraImport current={current} assist={perms.assist} />}

      <Tabs defaultValue="stable">
        <TabsList className="bg-[#F0EAE1] flex-wrap h-auto">
          <TabsTrigger value="stable" data-testid="tab-stable">Scuderia</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Categorie</TabsTrigger>
          <TabsTrigger value="activities" data-testid="tab-activities">Attività cavalli</TabsTrigger>
          <TabsTrigger value="people" data-testid="tab-people">Persone</TabsTrigger>
          {perms.canManage && <TabsTrigger value="treatments" data-testid="tab-treatments">Trattamenti</TabsTrigger>}
          <TabsTrigger value="members" data-testid="tab-members">Membri</TabsTrigger>
          <TabsTrigger value="legal" data-testid="tab-legal">Legal &amp; Privacy</TabsTrigger>
        </TabsList>

        <TabsContent value="stable" className="mt-4">
          <StableSettings current={current} canEdit={perms.canManage} canManage={perms.canManage} refresh={refresh} />
        </TabsContent>
        <TabsContent value="categories" className="mt-4 space-y-6">
          <CategoryManager title="Categorie cavalli" slug="horse-categories" current={current} canEdit={perms.canManage} />
          <CategoryManager title="Categorie scadenze" slug="deadline-categories" current={current} canEdit={perms.canManage} />
          <CategoryManager title="Tipi di intervento mascalcia" slug="farrier-types" current={current} canEdit={perms.canManage} />
        </TabsContent>
        <TabsContent value="activities" className="mt-4">
          <ActivityTypesManager current={current} canEdit={perms.canManage} />
        </TabsContent>
        <TabsContent value="people" className="mt-4">
          <PeopleMovedNote />
        </TabsContent>
        <TabsContent value="members" className="mt-4">
          <Members current={current} canManage={perms.canManage} />
        </TabsContent>
        <TabsContent value="legal" className="mt-4">
          <GestoreLegal />
        </TabsContent>
        {perms.canManage && (
          <TabsContent value="treatments" className="mt-4">
            <TreatmentsManager current={current} canManage={perms.canManage} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function StableSettings({ current, canEdit, canManage, refresh }) {
  const FIELDS = [
    ["denominazione", "Denominazione / Ragione sociale"], ["asl_code", "Codice stalla"],
    ["codice_fiscale", "Codice fiscale"], ["partita_iva", "Partita IVA"],
    ["address", "Indirizzo"], ["cap", "CAP"], ["comune", "Comune"], ["provincia", "Provincia"],
    ["regione", "Regione"], ["nazione", "Nazione"], ["telefono", "Telefono"], ["email", "Email"],
    ["pec", "PEC"], ["sito_web", "Sito web"], ["codice_sdi", "Codice SDI"], ["referente", "Referente principale"],
  ];
  const [form, setForm] = useState({
    name: current.name, logo: current.logo || null, contact: current.contact || "", notes: current.notes || "",
    ...Object.fromEntries(FIELDS.map(([k]) => [k, current[k] || ""])),
  });
  const [saving, setSaving] = useState(false);
  const [adminReq, setAdminReq] = useState(null);
  const logoRef = useRef(null);
  const [logoUp, setLogoUp] = useState(false);
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setLogoUp(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo: data.path }));
      toast.success("Logo caricato. Ricordati di salvare.");
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setLogoUp(false); }
  };
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/stables/${current.id}`, form);
      await refresh();
      toast.success("Scuderia aggiornata");
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const sendAdminReq = async () => {
    try {
      await api.post(`/stables/${current.id}/admin-requests`, { type: adminReq });
      toast.success("Richiesta inviata al Platform Admin. Nessun dato è stato modificato.");
      setAdminReq(null);
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6">
      <form onSubmit={save} className="bg-white rounded-xl border border-[#E5DEC9] p-6 space-y-4">
        <div>
          <Label>Logo scuderia</Label>
          <div className="mt-1.5 flex items-center gap-4">
            <div className="h-16 w-16 rounded-lg bg-[#F0EAE1] border border-[#E5DEC9] overflow-hidden flex items-center justify-center shrink-0">
              {form.logo ? <img src={resolveImage(form.logo)} alt="Logo" className="h-full w-full object-contain" data-testid="settings-logo-preview" />
                : <span className="text-[10px] text-[#8C532B] font-mono-label">Logo</span>}
            </div>
            {canEdit && (
              <div className="flex flex-wrap gap-2">
                <input ref={logoRef} type="file" accept="image/jpeg,image/png,image/webp" hidden data-testid="settings-logo-input" onChange={uploadLogo} />
                <Button type="button" variant="outline" size="sm" disabled={logoUp} data-testid="settings-logo-upload" onClick={() => logoRef.current?.click()}>
                  {logoUp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}{form.logo ? "Sostituisci" : "Carica logo"}
                </Button>
                {form.logo && <Button type="button" variant="outline" size="sm" data-testid="settings-logo-remove" onClick={() => setForm((f) => ({ ...f, logo: null }))} className="text-red-600">Rimuovi</Button>}
              </div>
            )}
          </div>
          <p className="text-xs text-[#9AA39C] mt-1.5">Compare in modo discreto accanto al nome della scuderia. Ricordati di salvare.</p>
        </div>
        <div><Label>Nome visualizzato *</Label><Input className="mt-1.5" disabled={!canEdit} data-testid="settings-stable-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, label]) => (
            <div key={k}>
              <Label>{label}</Label>
              <Input className="mt-1.5" disabled={!canEdit} data-testid={`settings-stable-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            </div>
          ))}
        </div>
        <div><Label>Note</Label><Textarea className="mt-1.5" disabled={!canEdit} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        <div className="text-xs text-[#5F6B64] font-mono-label" data-testid="stable-internal-id">ID interno Equiro: {current.id}</div>
        {canEdit && (
          <Button type="submit" disabled={saving} data-testid="save-stable-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Salva modifiche
          </Button>
        )}
      </form>

      {canManage && (
        <div className="bg-white rounded-xl border border-red-200 p-6" data-testid="stable-admin-zone">
          <h3 className="font-display font-semibold text-red-700 mb-1">Zona amministrativa</h3>
          <p className="text-sm text-[#5F6B64] mb-4">Queste sono solo richieste inviate al Platform Admin: non disattivano né eliminano nulla in automatico.</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" data-testid="request-deactivate-stable" onClick={() => setAdminReq("deactivate")} className="border-red-300 text-red-700 hover:bg-red-50">Richiedi disattivazione scuderia</Button>
            <Button variant="outline" data-testid="request-delete-stable" onClick={() => setAdminReq("delete")} className="border-red-300 text-red-700 hover:bg-red-50">Richiedi eliminazione scuderia</Button>
          </div>
        </div>
      )}

      {adminReq && (
        <Dialog open={!!adminReq} onOpenChange={(o) => !o && setAdminReq(null)}>
          <DialogContent data-testid="stable-admin-req-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">{adminReq === "delete" ? "Richiedere l'eliminazione della scuderia?" : "Richiedere la disattivazione della scuderia?"}</DialogTitle>
              <DialogDescription>
                Verrà creata una richiesta pendente per il Platform Admin. Nessun dato viene modificato o eliminato ora.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAdminReq(null)} data-testid="stable-admin-req-cancel">Annulla</Button>
              <Button onClick={sendAdminReq} data-testid="stable-admin-req-confirm" className="bg-red-600 hover:bg-red-700 text-white">Invia richiesta</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function LegacyResume({ current, assist }) {
  const [pkg, setPkg] = useState(null);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState({});
  const [corr, setCorr] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    if (!current) return;
    api.get(`/stables/${current.id}/legacy-import`).then((r) => setPkg(r.data.package)).catch(() => setPkg(null));
  }, [current]);
  useEffect(() => { load(); }, [load]);
  if (!pkg || pkg.status === "completed") return null;
  const cands = pkg.candidates || [];
  const effImportable = (c) => c.importable || !!(corr[c.candidate_id] || "").trim();
  const openDlg = () => { const i = {}; cands.forEach((c) => { i[c.candidate_id] = !!c.importable; }); setSel(i); setOpen(true); };
  const selectedCount = cands.filter((c) => sel[c.candidate_id]).length;
  const doImport = async () => {
    setBusy(true);
    try {
      const selected_ids = cands.filter((c) => sel[c.candidate_id]).map((c) => c.candidate_id);
      const corrections = {}; Object.entries(corr).forEach(([k, v]) => { if ((v || "").trim()) corrections[k] = v.trim(); });
      const { data } = await api.post(`/stables/${current.id}/legacy-import/import`, { selected_ids, corrections });
      const s = data.summary || {};
      toast.success(`Importati ${(s.imported_new_identity || 0) + (s.associated_existing_identity || 0)} cavalli`);
      window.dispatchEvent(new Event("equiro-legacy-refresh"));
      setOpen(false); setPkg(null);
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-[#C9A227]/50 bg-[#FBF6E7] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="legacy-resume-banner">
      <div>
        <p className="font-display font-semibold text-[#1B3B2B]">Cavalli dal tuo precedente Equiro</p>
        <p className="text-sm text-[#5F6B64]">{pkg.counts?.importable ?? cands.length} cavalli pronti da importare · scuderia {pkg.legacy_stable_id}</p>
      </div>
      {assist ? (
        <span data-testid="legacy-resume-assist-note" className="text-xs italic text-[#8C6D4F]">Visibile in modalità Assistenza — azione riservata al Gestore</span>
      ) : (
        <Button data-testid="legacy-resume-open" onClick={openDlg} className="bg-[#1B3B2B] hover:bg-[#142E1F]">Riprendi import</Button>
      )}
      <Dialog open={open && !assist} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="legacy-resume-dialog">
          <DialogHeader><DialogTitle className="font-display">Importa cavalli legacy</DialogTitle>
            <DialogDescription className="text-xs">Seleziona i cavalli che fanno ancora parte della tua scuderia.</DialogDescription></DialogHeader>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-[#1B3B2B]" data-testid="legacy-resume-count">{selectedCount} selezionati</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { const n = {}; cands.forEach((c) => { n[c.candidate_id] = effImportable(c); }); setSel(n); }}>Tutti</Button>
              <Button size="sm" variant="outline" onClick={() => setSel({})}>Nessuno</Button>
            </div>
          </div>
          <div className="space-y-1.5 max-h-[55vh] overflow-y-auto">
            {cands.map((c) => {
              const badName = c.issues?.includes("nome_mancante") && !(corr[c.candidate_id] || "").trim();
              return (
                <div key={c.candidate_id} data-testid={`resume-cand-${c.candidate_id}`} className="flex items-start gap-2 rounded-lg border border-[#E5DEC9] bg-white px-3 py-2">
                  <input type="checkbox" disabled={!effImportable(c)} checked={!!sel[c.candidate_id]} onChange={() => setSel((s) => ({ ...s, [c.candidate_id]: !s[c.candidate_id] }))}
                    data-testid={`resume-sel-${c.candidate_id}`} className="mt-1 h-4 w-4 accent-[#1B3B2B] disabled:opacity-40" />
                  <div className="min-w-0 flex-1">
                    {badName ? (
                      <Input placeholder="Nome mancante — correggi" value={corr[c.candidate_id] || ""} data-testid={`resume-fix-${c.candidate_id}`}
                        onChange={(e) => setCorr((s) => ({ ...s, [c.candidate_id]: e.target.value }))} className="h-8 text-sm" />
                    ) : <p className="font-medium text-[#1B3B2B] truncate">{corr[c.candidate_id]?.trim() || c.name}</p>}
                    <p className="text-[11px] text-[#8C6D4F]">{[c.sex, c.dob ? new Date(c.dob).getFullYear() : null, c.breed, c.microchip ? `chip ${c.microchip}` : null].filter(Boolean).join(" · ")}</p>
                    {c.legacy_status && <p className="text-[10px] text-[#9AA39C]">Stato nel precedente Equiro: {c.legacy_status}</p>}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Chiudi</Button>
            <Button onClick={doImport} disabled={busy || selectedCount === 0} data-testid="legacy-resume-import" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Importa ({selectedCount})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const EXTRA_GROUPS = [
  { key: "health", label: "Scadenze sanitarie" },
  { key: "deworming", label: "Antiparassitari" },
  { key: "farrier", label: "Mascalcia" },
];

function LegacyExtraImport({ current, assist }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const navigate = useNavigate();
  const load = useCallback(() => {
    if (!current) return;
    api.get(`/stables/${current.id}/legacy-import/extra`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [current]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("equiro-legacy-refresh", h);
    return () => window.removeEventListener("equiro-legacy-refresh", h);
  }, [load]);
  if ((!data || !data.package) && !summary) return null;
  const counts = (data && data.counts) || {};
  const pendingSafe = (counts.health || 0) + (counts.deworming || 0) + (counts.farrier || 0);
  const items = (data && data.items) || {};
  const verifyList = items.verify || [];
  const showBanner = !!(data && data.package && (pendingSafe > 0 || (counts.verify || 0) > 0));
  const fmtY = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");
  const openDlg = () => {
    const s = {};
    ["health", "deworming", "farrier"].forEach((g) => (items[g] || []).forEach((it) => { if (it.safe && !it.already) s[it.key] = true; }));
    setSel(s); setOpen(true);
  };
  const toggle = (k) => setSel((s) => ({ ...s, [k]: !s[k] }));
  const selectedKeys = () => Object.entries(sel).filter(([, v]) => v).map(([k]) => k);
  const doImport = async () => {
    setBusy(true);
    try {
      const keys = selectedKeys();
      const touched = new Set(keys.map((k) => k.split(":")[1]));
      const mark_completed = ["health", "deworming", "farrier"].filter((g) => touched.has(g));
      const { data: res } = await api.post(`/stables/${current.id}/legacy-import/extra/import`, { keys, mark_completed });
      const r = res.result || {};
      setOpen(false);
      setSummary({ ...r, verify: verifyList.length });
      load();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const selCount = selectedKeys().length;
  return (
    <>
    {showBanner && (
    <div className="rounded-xl border border-[#C9A227]/50 bg-[#FBF6E7] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="legacy-extra-banner">
      <div>
        <p className="font-display font-semibold text-[#1B3B2B]">Abbiamo trovato altri dati del tuo precedente Equiro</p>
        <p className="text-sm text-[#5F6B64]" data-testid="legacy-extra-counts">
          {counts.health || 0} scadenze sanitarie · {counts.deworming || 0} antiparassitari · {counts.farrier || 0} mascalcia{(counts.verify || 0) > 0 ? ` · ${counts.verify} da verificare` : ""}
        </p>
      </div>
      {assist ? (
        <span data-testid="legacy-extra-assist-note" className="text-xs italic text-[#8C6D4F]">Visibile in modalità Assistenza — azione riservata al Gestore</span>
      ) : (
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setData(null)} data-testid="legacy-extra-skip">Salta per ora</Button>
          <Button data-testid="legacy-extra-open" onClick={openDlg} className="bg-[#1B3B2B] hover:bg-[#142E1F]">Rivedi e importa</Button>
        </div>
      )}
      <Dialog open={open && !assist} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="legacy-extra-dialog">
          <DialogHeader><DialogTitle className="font-display">Dati dal precedente Equiro</DialogTitle>
            <DialogDescription className="text-xs">Solo dati reali. Nessuna scadenza o ricorrenza è stata inventata. I record dubbi sono marcati «da verificare» e non selezionati.</DialogDescription></DialogHeader>
          <div className="space-y-4 max-h-[62vh] overflow-y-auto">
            {EXTRA_GROUPS.map((g) => {
              const list = items[g.key] || [];
              if (list.length === 0) return null;
              return (
                <div key={g.key} data-testid={`legacy-extra-group-${g.key}`}>
                  <p className="font-mono-label text-xs text-[#8C532B] mb-1">{g.label} ({list.length})</p>
                  <div className="space-y-1.5">
                    {list.map((it) => {
                      const disabled = it.already || it.conflict;
                      return (
                        <label key={it.key} data-testid={`legacy-extra-item-${it.key}`} className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${it.already ? "border-[#E5DEC9] bg-[#F3EFE4] opacity-70" : "border-[#E5DEC9] bg-white"}`}>
                          <input type="checkbox" disabled={disabled} checked={!!sel[it.key]} onChange={() => toggle(it.key)} data-testid={`legacy-extra-sel-${it.key}`} className="mt-1 h-4 w-4 accent-[#1B3B2B] disabled:opacity-40" />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-[#1B3B2B] truncate">{it.horse_name}</p>
                            <p className="text-[11px] text-[#8C6D4F]">
                              {g.key === "health" && `${it.category} · scade ${fmtY(it.due_date)}`}
                              {g.key === "deworming" && `${fmtY(it.date)}${it.product ? ` · ${it.product}` : ""}`}
                              {g.key === "farrier" && `${it.work_type || "Intervento"} · ${fmtY(it.date)}`}
                            </p>
                            {it.already && <p className="text-[10px] text-emerald-700">Già importato</p>}
                            {it.conflict && <p className="text-[10px] text-amber-700">Già presente in Equiro — dato nuovo preservato</p>}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {verifyList.length > 0 && (
              <div data-testid="legacy-extra-group-verify">
                <p className="font-mono-label text-xs text-[#B45309] mb-1">⚠️ Da verificare ({verifyList.length})</p>
                <p className="text-[11px] text-[#8C6D4F] mb-1.5">Dati legacy reali ma non abbastanza affidabili per l'import automatico. Sistemali manualmente nella relativa sezione.</p>
                <div className="space-y-1.5">
                  {verifyList.map((it, i) => (
                    <div key={i} data-testid={`legacy-verify-item-${i}`} className="rounded-lg border border-[#F0D9A8] bg-[#FCF6E8] px-3 py-2">
                      <p className="font-medium text-[#1B3B2B] truncate">{it.horse_name} <span className="text-[11px] text-[#8C6D4F]">· {it.label}</span></p>
                      <p className="text-[11px] text-[#B45309]">
                        {it.reason === "vermifugo_incongruente" && `Vermifugo incongruente: V1 ${fmtY(it.v1_date)}${it.v1_product ? ` (${it.v1_product})` : ""} più recente di V2 ${fmtY(it.v2_date)}${it.v2_product ? ` (${it.v2_product})` : ""}`}
                        {it.reason === "data_non_interpretata" && "Data legacy non interpretabile in sicurezza"}
                        {it.reason === "solo_flag_legacy" && "Solo flag legacy attivo, senza data"}
                      </p>
                      {it.horse_id && <button data-testid={`legacy-verify-open-${i}`} onClick={() => navigate(`/cavalli/${it.horse_id}`)} className="mt-1 text-[11px] text-[#1B3B2B] underline">Apri cavallo</button>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Chiudi</Button>
            <Button onClick={doImport} disabled={busy || selCount === 0} data-testid="legacy-extra-import" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Importa ({selCount})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    )}
      <Dialog open={!!summary} onOpenChange={(o) => !o && setSummary(null)}>
        <DialogContent data-testid="legacy-extra-summary" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Importazione completata</DialogTitle>
            <DialogDescription className="text-xs">Dal precedente Equiro abbiamo recuperato:</DialogDescription></DialogHeader>
          {summary && (
            <div className="space-y-1.5 text-sm text-[#1B3B2B]" data-testid="legacy-summary-body">
              <p>· <b>{summary.health || 0}</b> scadenze sanitarie</p>
              <p>· <b>{summary.deworming || 0}</b> trattamenti antiparassitari</p>
              <p>· <b>{summary.farrier || 0}</b> interventi di mascalcia</p>
              {(summary.skipped_conflict || 0) > 0 && <p className="text-[#8C6D4F]">· {summary.skipped_conflict} già presenti in Equiro (preservati)</p>}
              {(summary.verify || 0) > 0 && <p className="text-[#B45309]" data-testid="legacy-summary-verify">⚠️ {summary.verify} dati richiedono una verifica</p>}
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" data-testid="legacy-summary-scadenze" onClick={() => navigate("/scadenze")}>Vai allo Scadenzario</Button>
            <Button variant="outline" data-testid="legacy-summary-mascalcia" onClick={() => navigate("/mascalcia")}>Vai alla Mascalcia</Button>
            {(summary?.verify || 0) > 0 && <Button variant="outline" data-testid="legacy-summary-review" onClick={() => { setSummary(null); openDlg(); }}>Rivedi dati da verificare</Button>}
            <Button onClick={() => setSummary(null)} data-testid="legacy-summary-close" className="bg-[#1B3B2B] hover:bg-[#142E1F]">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


const ACTIVITY_GROUPS = [
  { key: "box", label: "Gestione box", hint: "Nessun tipo predefinito: crea tu i tipi di gestione box della scuderia." },
  { key: "preparazione", label: "Preparazione / gestione cavallo", hint: null },
  { key: "attivita", label: "Attività con il cavallo", hint: null },
];

function PeopleMovedNote() {
  const navigate = useNavigate();
  return (
    <div className="rounded-xl border border-[#E5DEC9] bg-white p-5 space-y-3" data-testid="people-moved-note">
      <h3 className="font-display text-lg font-bold text-[#1B3B2B]">Persone</h3>
      <p className="text-sm text-[#5F6B64]">
        La gestione delle persone (anagrafica, ricerca, unione, archiviazione ed eliminazione) è ora
        centralizzata nella sezione <strong>Persone</strong>. Da lì puoi gestire anche le Organizzazioni.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="people-moved-goto" onClick={() => navigate("/persone")} className="bg-[#1B3B2B] hover:bg-[#142E1F]">
          Vai a Persone
        </Button>
        <Button variant="outline" data-testid="orgs-moved-goto" onClick={() => navigate("/organizzazioni")}>
          Organizzazioni
        </Button>
      </div>
    </div>
  );
}

function ActivityTypesManager({ current, canEdit }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addName, setAddName] = useState({});
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [cfg, setCfg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/stables/${current.id}/activity-types?include_archived=true`);
      setTypes(r.data || []);
      api.get(`/stables/${current.id}/activity-config`).then((c) => setCfg(c.data)).catch(() => setCfg(null));
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [current.id]);
  useEffect(() => { load(); }, [load]);

  const toggleAlert = async (val) => {
    try { const r = await api.put(`/stables/${current.id}/activity-config`, { no_activity_alert: val }); setCfg(r.data); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const setFlag = async (t, patch) => {
    try { await api.patch(`/stables/${current.id}/activity-types/${t.id}/flags`, patch); load(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };

  const handleErr = (e) => {
    const d = e.response?.data?.detail;
    toast.error(typeof d === "object" ? (d?.message || "Errore") : errText(d));
  };

  const add = async (resp) => {
    const name = (addName[resp] || "").trim();
    if (!name) return;
    try {
      await api.post(`/stables/${current.id}/activity-types`, { responsibility: resp, name });
      setAddName((s) => ({ ...s, [resp]: "" }));
      load();
    } catch (e) { handleErr(e); }
  };
  const saveRename = async (t) => {
    const name = editName.trim();
    if (!name) return;
    try {
      await api.put(`/stables/${current.id}/activity-types/${t.id}`, { name });
      setEditing(null); setEditName(""); load();
    } catch (e) { handleErr(e); }
  };
  const archive = async (t) => {
    try { await api.post(`/stables/${current.id}/activity-types/${t.id}/archive`); load(); }
    catch (e) { handleErr(e); }
  };
  const restore = async (t) => {
    try { await api.post(`/stables/${current.id}/activity-types/${t.id}/restore`); load(); }
    catch (e) { handleErr(e); }
  };
  const move = async (resp, list, idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const ids = list.map((t) => t.id);
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try { await api.put(`/stables/${current.id}/activity-types-order`, { responsibility: resp, order: ids }); load(); }
    catch (e) { handleErr(e); }
  };

  return (
    <div className="space-y-6" data-testid="activity-types-manager">
      <div>
        <h3 className="font-display text-lg font-bold text-[#1B3B2B]">Attività cavalli</h3>
        <p className="text-sm text-[#5F6B64]">Configura la tassonomia dei tipi attività per le tre aree di responsabilità. Le lezioni restano nel Calendario e non sono un tipo attività.</p>
      </div>
      {canEdit && (
        <div className="rounded-xl border border-[#E5DEC9] bg-white p-3 flex items-start gap-3" data-testid="no-activity-config">
          <input type="checkbox" className="mt-1" data-testid="no-activity-toggle" checked={!!cfg?.no_activity_alert} onChange={(e) => toggleAlert(e.target.checked)} />
          <div>
            <p className="text-sm font-medium text-[#1B3B2B]">Avvisami se un cavallo presente non ha attività/uscite previste nella giornata</p>
            <p className="text-xs text-[#8C6D4F] mt-0.5">Opzionale. Contano come uscita i tipi contrassegnati sotto; "Riposo" esenta il cavallo. Le lezioni Calendar contano sempre.</p>
          </div>
        </div>
      )}
      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#8C532B]" /></div>
      ) : ACTIVITY_GROUPS.map((g) => {
        const activeList = types.filter((t) => t.responsibility === g.key && !t.archived);
        const archivedList = types.filter((t) => t.responsibility === g.key && t.archived);
        return (
          <div key={g.key} data-testid={`activity-group-${g.key}`} className="rounded-xl border border-[#E5DEC9] bg-white p-4 sm:p-5">
            <p className="font-mono-label text-[10px] uppercase tracking-wide text-[#8C532B]">{g.label}</p>
            {g.hint && <p className="text-xs text-[#9AA39C] mt-0.5">{g.hint}</p>}

            <div className="mt-3 space-y-1.5">
              {activeList.length === 0 && <p className="text-sm text-[#9AA39C]">Nessun tipo attività.</p>}
              {activeList.map((t, idx) => (
                <div key={t.id} data-testid={`at-row-${t.id}`} className="rounded-lg px-2 py-1.5 hover:bg-[#F7F3EC]">
                  <div className="flex items-center gap-2">
                    {editing === t.id ? (
                      <>
                        <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8 flex-1" data-testid={`at-rename-input-${t.id}`} />
                        <button onClick={() => saveRename(t)} data-testid={`at-rename-save-${t.id}`} className="text-green-700 hover:text-green-800"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setEditing(null); setEditName(""); }} data-testid={`at-rename-cancel-${t.id}`} className="text-[#8C532B] hover:text-red-600"><X className="h-4 w-4" /></button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm text-[#1B3B2B]" data-testid={`at-name-${t.id}`}>{t.name}</span>
                        {canEdit && (
                          <>
                            <button onClick={() => move(g.key, activeList, idx, -1)} disabled={idx === 0} data-testid={`at-up-${t.id}`} className="text-[#8C6D4F] hover:text-[#5F4626] disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                            <button onClick={() => move(g.key, activeList, idx, 1)} disabled={idx === activeList.length - 1} data-testid={`at-down-${t.id}`} className="text-[#8C6D4F] hover:text-[#5F4626] disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                            <button onClick={() => { setEditing(t.id); setEditName(t.name); }} data-testid={`at-rename-${t.id}`} className="text-[#8C6D4F] hover:text-[#5F4626]"><Pencil className="h-3.5 w-3.5" /></button>
                            <button onClick={() => archive(t)} data-testid={`at-archive-${t.id}`} title="Archivia" className="text-[#8C6D4F] hover:text-[#5F4626]"><Archive className="h-3.5 w-3.5" /></button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  {editing !== t.id && canEdit && g.key === "attivita" && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 pl-1" data-testid={`at-flags-${t.id}`}>
                      <label className="flex items-center gap-1.5 text-xs text-[#5F6B64] cursor-pointer">
                        <input type="checkbox" data-testid={`at-flag-outing-${t.id}`} checked={!!t.counts_as_outing} disabled={!!t.is_rest}
                          onChange={(e) => setFlag(t, { counts_as_outing: e.target.checked })} />
                        <span title="Se attivo, questa attività evita che il cavallo venga segnalato come «senza attività prevista».">Conta come uscita giornaliera</span>
                      </label>
                      <label className="flex items-center gap-1.5 text-xs text-[#5F6B64] cursor-pointer">
                        <input type="checkbox" data-testid={`at-flag-rest-${t.id}`} checked={!!t.is_rest}
                          onChange={(e) => setFlag(t, e.target.checked ? { is_rest: true, counts_as_outing: false } : { is_rest: false })} />
                        <span title="Indica che il cavallo è intenzionalmente a riposo e non deve essere segnalato come senza attività.">Riposo</span>
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {canEdit && (
              <div className="mt-3 flex items-center gap-2">
                <Input value={addName[g.key] || ""} onChange={(e) => setAddName((s) => ({ ...s, [g.key]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(g.key); } }}
                  placeholder="Nuovo tipo attività…" className="h-9 flex-1" data-testid={`at-add-input-${g.key}`} />
                <Button size="sm" onClick={() => add(g.key)} data-testid={`at-add-btn-${g.key}`} className="bg-[#1B3B2B] hover:bg-[#142E1F]">
                  <Plus className="h-4 w-4 mr-1" /> Aggiungi
                </Button>
              </div>
            )}

            {archivedList.length > 0 && (
              <div className="mt-4 pt-3 border-t border-[#E5DEC9]" data-testid={`at-archived-${g.key}`}>
                <p className="font-mono-label text-[10px] uppercase tracking-wide text-[#9AA39C] mb-1.5">Archiviati</p>
                <div className="space-y-1">
                  {archivedList.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 text-sm text-[#9AA39C]">
                      <span className="flex-1 line-through">{t.name}</span>
                      {canEdit && (
                        <button onClick={() => restore(t)} data-testid={`at-restore-${t.id}`} title="Ripristina" className="text-[#8C6D4F] hover:text-[#5F4626]"><RotateCcw className="h-3.5 w-3.5" /></button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CategoryManager({ title, slug, current, canEdit }) {
  const manageable = slug === "deadline-categories";
  const priorityEnabled = slug === "deadline-categories";
  // Horse categories share the safe Archivia/Ripristina path (but not rename/priority).
  const archivable = manageable || slug === "horse-categories";
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [rename, setRename] = useState(null); // {id, oldName, name}
  const [renaming, setRenaming] = useState(false);
  const [restorePrompt, setRestorePrompt] = useState(null); // {category_id, name}
  const load = useCallback(() => {
    api.get(`/stables/${current.id}/${slug}`).then((r) => setItems(r.data));
  }, [current, slug]);
  useEffect(() => { load(); }, [load]);

  const visible = archivable ? items.filter((c) => !c.archived) : items;
  const archivedItems = archivable ? items.filter((c) => c.archived) : [];
  const prioritized = visible.filter((c) => c.priority).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const normal = visible.filter((c) => !c.priority).sort((a, b) => a.name.localeCompare(b.name));

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post(`/stables/${current.id}/${slug}`, { name: name.trim() });
      setName(""); load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === "archived_exists") { setRestorePrompt({ category_id: d.category_id, name: d.name }); return; }
      toast.error(errText(d));
    }
  };
  const remove = async (c) => {
    if (!window.confirm(`Eliminare «${c.name}»?`)) return;
    try {
      await api.delete(`/stables/${current.id}/${slug}/${c.id}`); load();
      toast.success("Categoria eliminata");
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === "referenced") { toast.error(d.message); return; }
      toast.error(errText(d));
    }
  };
  const archive = async (c) => {
    try { await api.post(`/stables/${current.id}/${slug}/${c.id}/archive`); load(); toast.success("Categoria archiviata"); }
    catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };
  const restore = async (id) => {
    try { await api.post(`/stables/${current.id}/${slug}/${id}/restore`); setRestorePrompt(null); load(); toast.success("Categoria ripristinata"); }
    catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };
  const doRename = async () => {
    if (!rename?.name?.trim()) return;
    setRenaming(true);
    try {
      await api.post(`/stables/${current.id}/${slug}/${rename.id}/rename`, { name: rename.name.trim() });
      toast.success("Categoria rinominata"); setRename(null); load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === "archived_exists") { setRename(null); setRestorePrompt({ category_id: d.category_id, name: d.name }); return; }
      toast.error(errText(d));
    } finally { setRenaming(false); }
  };
  const togglePriority = async (c) => {
    const enabling = !c.priority;
    await api.put(`/stables/${current.id}/${slug}/${c.id}`,
      { name: c.name, priority: enabling, order: enabling ? prioritized.length : 0 });
    load();
  };
  const move = async (idx, dir) => {
    const arr = [...prioritized];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    await api.put(`/stables/${current.id}/${slug}-order`, { order: arr.map((c) => c.id) });
    load();
  };

  const catActions = (c) => (manageable && canEdit) ? (
    <>
      <button data-testid={`rename-cat-${c.id}`} onClick={() => setRename({ id: c.id, oldName: c.name, name: c.name })}
        className="p-1 text-[#5F6B64] hover:text-[#1B3B2B]" title="Rinomina"><Pencil className="h-3.5 w-3.5" /></button>
      <button data-testid={`archive-cat-${c.id}`} onClick={() => archive(c)}
        className="p-1 text-[#8C6D4F] hover:text-[#5F4626]" title="Archivia"><Archive className="h-3.5 w-3.5" /></button>
      <button data-testid={`del-cat-${c.id}`} onClick={() => remove(c)}
        className="p-1 text-[#8C532B] hover:text-red-600" title="Elimina"><Trash2 className="h-3.5 w-3.5" /></button>
    </>
  ) : null;

  return (
    <div className="bg-white rounded-xl border border-[#E5DEC9] p-6">
      <h3 className="font-display font-semibold text-[#1B3B2B] mb-3">{title}</h3>

      {priorityEnabled && prioritized.length > 0 && (
        <div className="mb-4">
          <p className="font-mono-label text-[10px] text-[#8C532B] mb-2">Categorie prioritarie (ordine di visualizzazione delle scadute)</p>
          <div className="space-y-1.5">
            {prioritized.map((c, i) => (
              <div key={c.id} data-testid={`prio-cat-${c.id}`}
                className="flex items-center gap-2 rounded-lg border border-[#E5DEC9] bg-[#FCFAF6] px-3 py-1.5">
                <span className="text-xs font-semibold text-[#8C532B] w-5">{i + 1}.</span>
                <span className="flex-1 text-sm text-[#1B3B2B] truncate">{c.name}</span>
                {canEdit && (
                  <>
                    <button data-testid={`prio-up-${c.id}`} disabled={i === 0} onClick={() => move(i, -1)}
                      className="p-1 disabled:opacity-30 text-[#5F6B64] hover:text-[#1B3B2B]"><ChevronUp className="h-4 w-4" /></button>
                    <button data-testid={`prio-down-${c.id}`} disabled={i === prioritized.length - 1} onClick={() => move(i, 1)}
                      className="p-1 disabled:opacity-30 text-[#5F6B64] hover:text-[#1B3B2B]"><ChevronDown className="h-4 w-4" /></button>
                    <button data-testid={`unprio-${c.id}`} onClick={() => togglePriority(c)}
                      className="p-1 text-[#C9A227] hover:text-[#8C532B]" title="Rimuovi priorità"><Star className="h-4 w-4 fill-current" /></button>
                    {catActions(c)}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {manageable ? (
        <div className="space-y-1.5 mb-4">
          {normal.map((c) => (
            <div key={c.id} data-testid={`cat-${slug}-${c.id}`}
              className="flex items-center gap-2 rounded-lg border border-[#E5DEC9] bg-[#FCFAF6] px-3 py-1.5">
              <span className="flex-1 text-sm text-[#1B3B2B] truncate">{c.name}</span>
              {canEdit && (
                <>
                  <button data-testid={`prio-toggle-${c.id}`} onClick={() => togglePriority(c)}
                    className="p-1 text-[#B99A54] hover:text-[#C9A227]" title="Segna come prioritaria"><Star className="h-3.5 w-3.5" /></button>
                  {catActions(c)}
                </>
              )}
            </div>
          ))}
          {visible.length === 0 && <p className="text-sm text-[#5F6B64]">Nessuna categoria.</p>}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 mb-4">
          {normal.map((c) => (
            <span key={c.id} data-testid={`cat-${slug}-${c.id}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#F0EAE1] text-[#1B3B2B] px-3 py-1 text-sm">
              {c.name}
              {canEdit && priorityEnabled && (
                <button data-testid={`prio-toggle-${c.id}`} onClick={() => togglePriority(c)}
                  className="text-[#B99A54] hover:text-[#C9A227]" title="Segna come prioritaria"><Star className="h-3.5 w-3.5" /></button>
              )}
              {canEdit && archivable && (
                <button onClick={() => archive(c)} data-testid={`archive-cat-${c.id}`} title="Archivia" className="text-[#8C6D4F] hover:text-[#5F4626]">
                  <Archive className="h-3.5 w-3.5" />
                </button>
              )}
              {canEdit && (
                <button onClick={() => remove(c)} data-testid={`del-cat-${c.id}`} className="text-[#8C532B] hover:text-red-600">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          ))}
          {items.length === 0 && <p className="text-sm text-[#5F6B64]">Nessuna categoria.</p>}
        </div>
      )}

      {archivable && archivedItems.length > 0 && (
        <div className="mb-4">
          <p className="font-mono-label text-[10px] text-[#8C6D4F] mb-2 flex items-center gap-1"><Archive className="h-3 w-3" /> Archiviate</p>
          <div className="space-y-1.5">
            {archivedItems.map((c) => (
              <div key={c.id} data-testid={`archived-cat-${c.id}`}
                className="flex items-center gap-2 rounded-lg border border-dashed border-[#DED6C2] bg-[#FAF8F3] px-3 py-1.5">
                <span className="flex-1 text-sm text-[#8C6D4F] truncate">{c.name}</span>
                {canEdit && (
                  <>
                    <button data-testid={`restore-cat-${c.id}`} onClick={() => restore(c.id)}
                      className="inline-flex items-center gap-1 text-xs text-[#1F7A45] hover:text-[#155e35]" title="Ripristina"><RotateCcw className="h-3.5 w-3.5" /> Ripristina</button>
                    <button data-testid={`del-archived-cat-${c.id}`} onClick={() => remove(c)}
                      className="p-1 text-[#8C532B] hover:text-red-600" title="Elimina"><Trash2 className="h-3.5 w-3.5" /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {canEdit && (
        <form onSubmit={add} className="flex gap-2">
          <Input placeholder="Nuova voce…" value={name} data-testid={`add-cat-input-${slug}`} onChange={(e) => setName(e.target.value)} />
          <Button type="submit" variant="outline" data-testid={`add-cat-btn-${slug}`}><Plus className="h-4 w-4" /></Button>
        </form>
      )}
      {priorityEnabled && canEdit && (
        <p className="text-xs text-[#9AA39C] mt-2">Le categorie prioritarie determinano l'ordine delle scadenze SCADUTE. Le prossime scadenze restano ordinate per data.</p>
      )}

      {/* Rename dialog */}
      <Dialog open={!!rename} onOpenChange={(o) => !o && setRename(null)}>
        <DialogContent data-testid="rename-cat-dialog">
          <DialogHeader><DialogTitle className="font-display">Rinomina categoria</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nuovo nome</Label>
              <Input className="mt-1.5" data-testid="rename-cat-input" value={rename?.name || ""}
                onChange={(e) => setRename({ ...rename, name: e.target.value })} />
            </div>
            <p className="text-xs text-[#8C6D4F] rounded-lg bg-[#FCFAF6] border border-[#E5DEC9] px-3 py-2">
              Questa modifica aggiornerà il nome della categoria in tutte le scadenze e nello storico collegato. L'identità della categoria e le date/stati non cambiano.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRename(null)}>Annulla</Button>
            <Button onClick={doRename} disabled={renaming || !rename?.name?.trim()} data-testid="rename-cat-confirm" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              {renaming && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archived-name conflict -> offer restore */}
      <Dialog open={!!restorePrompt} onOpenChange={(o) => !o && setRestorePrompt(null)}>
        <DialogContent data-testid="restore-prompt-dialog">
          <DialogHeader><DialogTitle className="font-display">Categoria archiviata</DialogTitle></DialogHeader>
          <p className="text-sm text-[#2D3832]">
            Esiste già una categoria <span className="font-semibold">«{restorePrompt?.name}»</span> ma è archiviata. Vuoi ripristinarla invece di crearne una nuova?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestorePrompt(null)}>Annulla</Button>
            <Button onClick={() => restore(restorePrompt.category_id)} data-testid="restore-prompt-confirm" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              <RotateCcw className="h-4 w-4 mr-2" /> Ripristina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Members({ current, canManage }) {
  const [members, setMembers] = useState([]);
  const [horses, setHorses] = useState([]);
  const [invites, setInvites] = useState([]);
  const [joinReqs, setJoinReqs] = useState([]);
  const [horseReqs, setHorseReqs] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("staff");
  const [inviteHorses, setInviteHorses] = useState([]);
  const [inviteQuery, setInviteQuery] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortKey, setSortKey] = useState("name");
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = useCallback(() => {
    api.get(`/stables/${current.id}/members`).then((r) => setMembers(r.data));
    api.get(`/stables/${current.id}/horses`).then((r) => setHorses(r.data)).catch(() => {});
    if (canManage) {
      api.get(`/stables/${current.id}/invitations`).then((r) => setInvites(r.data)).catch(() => {});
      api.get(`/stables/${current.id}/join-requests`).then((r) => setJoinReqs(r.data)).catch(() => {});
      api.get(`/stables/${current.id}/horse-requests`).then((r) => setHorseReqs(r.data)).catch(() => {});
    }
  }, [current, canManage]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      const payload = { email, role, horse_ids: role === "owner" ? inviteHorses : [] };
      const { data } = await api.post(`/stables/${current.id}/invitations`, payload);
      if (data.status !== "invited") {
        toast.success("Membro aggiornato");
      } else if (data.email_status === "sent") {
        toast.success("Invito creato e inviato via email.");
      } else if (data.email_status === "failed") {
        toast.warning("Invito creato, ma non è stato possibile inviare l'email.");
      } else {
        toast.success("Invito creato. L'utente sarà associato automaticamente alla registrazione.");
      }
      setEmail(""); setInviteHorses([]); setInviteQuery("");
      load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };

  const iq = inviteQuery.trim().toLowerCase();
  const inviteSuggestions = iq
    ? horses.filter((h) => !inviteHorses.includes(h.id) &&
        ((h.display_name || "").toLowerCase().includes(iq) || (h.microchip || "").toLowerCase().includes(iq))).slice(0, 6)
    : [];
  const horseName = (id) => (horses.find((h) => h.id === id) || {}).display_name || id;

  const act = async (url, okMsg) => {
    try { await api.post(url); toast.success(okMsg); load(); }
    catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };
  const approveJoin = async (req, r) => {
    try { await api.post(`/stables/${current.id}/join-requests/${req.id}/approve`, { role: r }); toast.success("Richiesta approvata"); load(); }
    catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };
  const cancelInvite = async (id) => {
    try { await api.delete(`/stables/${current.id}/invitations/${id}`); toast.success("Invito annullato"); load(); }
    catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };
  const removeMember = async () => {
    try {
      await api.delete(`/stables/${current.id}/members/${removeTarget.user_id}`);
      toast.success("Membro rimosso dalla scuderia");
      setRemoveTarget(null); load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };

  const mq = search.trim().toLowerCase();
  const sortedMembers = [...members]
    .filter((m) => (roleFilter === "all" || m.role === roleFilter) &&
      (!mq || (m.name || "").toLowerCase().includes(mq) || (m.email || "").toLowerCase().includes(mq)))
    .sort((a, b) => sortKey === "email"
      ? (a.email || "").localeCompare(b.email || "")
      : (a.name || "").localeCompare(b.name || ""));

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-[#E5DEC9] p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-display font-semibold text-[#1B3B2B]">Membri della scuderia</h3>
          <span data-testid="members-total" className="text-xs text-[#5F6B64]">{members.length} membri totali</span>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C532B]" />
            <Input data-testid="members-search" className="pl-9" placeholder="Cerca per nome o email…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[170px]" data-testid="members-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i ruoli</SelectItem>
              <SelectItem value="manager">Gestore</SelectItem>
              <SelectItem value="staff">Staff</SelectItem>
              <SelectItem value="owner">Proprietario cavallo</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortKey} onValueChange={setSortKey}>
            <SelectTrigger className="w-[150px]" data-testid="members-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Ordina: Nome</SelectItem>
              <SelectItem value="email">Ordina: Email</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-5 mb-4">
          {GROUPS.map((g) => {
            const total = members.filter((m) => g.roles.includes(m.role)).length;
            const items = sortedMembers.filter((m) => g.roles.includes(m.role));
            if (items.length === 0) return null;
            return (
              <div key={g.key} data-testid={`members-group-${g.key}`}>
                <p className="font-mono-label text-[10px] text-[#8C532B] mb-1.5">{g.label} ({total})</p>
                <div className="divide-y divide-[#F0EAE1]">
                  {items.map((m) => {
                    const on = m.last_activity_at && (Date.now() - new Date(m.last_activity_at).getTime()) < 300000;
                    const actLabel = on ? "Online" : (m.last_activity_at
                      ? `Ultima attività ${new Date(m.last_activity_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })} ${new Date(m.last_activity_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`
                      : "Nessuna attività");
                    return (
                    <div key={m.user_id} className="py-3" data-testid={`member-${m.user_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-[#1B3B2B] truncate">{m.name}</p>
                          {m.email && <p className="text-xs text-[#5F6B64] truncate">{m.email}</p>}
                          <p className="text-[11px] text-[#8C6D4F] inline-flex items-center gap-1" data-testid={`member-activity-${m.user_id}`}>{on && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />}{actLabel}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-mono-label text-[10px] rounded-full px-2 py-0.5 bg-[#F0EAE1] text-[#8C532B]">{ROLE_LABEL[m.role]}</span>
                          {canManage && (m.role === "staff" || m.role === "owner") && (
                            <button data-testid={`remove-member-${m.user_id}`} onClick={() => setRemoveTarget(m)} className="text-[#8C532B] hover:text-red-600" title="Rimuovi dalla scuderia">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      {m.role === "owner" && (
                        <OwnerHorses member={m} current={current} horses={horses} canManage={canManage} onSaved={load} />
                      )}
                    </div>
                  );})}
                </div>
              </div>
            );
          })}
          {members.length === 0 && <p className="text-sm text-[#5F6B64]">Nessun membro.</p>}
        </div>
        {canManage && (
          <div className="border-t border-[#F0EAE1] pt-4 space-y-3">
            <form onSubmit={add} className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label>Email utente (anche non ancora registrato)</Label>
                <Input type="email" required className="mt-1.5" data-testid="member-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@esempio.it" />
              </div>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="w-[160px]" data-testid="member-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Gestore</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="owner">Proprietario cavallo</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" data-testid="add-member-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]"><Plus className="h-4 w-4 mr-1" /> Invita</Button>
            </form>
            {role === "owner" && (
              <div className="rounded-md bg-[#FCFAF6] border border-[#E5DEC9] p-3" data-testid="invite-horse-picker">
                <p className="font-mono-label text-[10px] text-[#8C532B] mb-1.5">Cavalli da associare (opzionale)</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {inviteHorses.length === 0 && <span className="text-xs text-[#5F6B64]">Nessun cavallo selezionato.</span>}
                  {inviteHorses.map((id) => (
                    <span key={id} className="inline-flex items-center gap-1.5 rounded-full bg-[#F0EAE1] text-[#1B3B2B] px-2.5 py-0.5 text-xs">
                      {horseName(id)}
                      <button type="button" onClick={() => setInviteHorses(inviteHorses.filter((x) => x !== id))} className="text-[#8C532B] hover:text-red-600"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="relative max-w-sm">
                  <div className="flex items-center gap-2 rounded-md border border-[#E5DEC9] px-2.5 py-1.5 bg-white">
                    <Search className="h-3.5 w-3.5 text-[#8C532B]" />
                    <input data-testid="invite-horse-search" className="flex-1 text-sm outline-none bg-transparent" placeholder="Cerca per nome o microchip…" value={inviteQuery} onChange={(e) => setInviteQuery(e.target.value)} />
                  </div>
                  {inviteSuggestions.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full rounded-md border border-[#E5DEC9] bg-white shadow-lg overflow-hidden">
                      {inviteSuggestions.map((h) => (
                        <button type="button" key={h.id} data-testid={`invite-horse-option-${h.id}`} onClick={() => { setInviteHorses([...inviteHorses, h.id]); setInviteQuery(""); }} className="flex w-full items-center justify-between px-3 py-2 text-sm text-left hover:bg-[#F0EAE1]">
                          <span className="text-[#1B3B2B]">{h.display_name}</span>
                          {h.microchip && <span className="text-xs text-[#5F6B64]">{h.microchip}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {canManage && (
        <RequestPanel title="Inviti pendenti" testid="invites-panel" empty="Nessun invito pendente.">
          {invites.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between py-2.5" data-testid={`invite-${inv.id}`}>
              <div>
                <p className="text-sm font-medium text-[#1B3B2B]">{inv.email}</p>
                <p className="text-xs text-[#5F6B64]">{ROLE_LABEL[inv.role]}{inv.horse_names?.length ? ` · ${inv.horse_names.join(", ")}` : ""}</p>
              </div>
              <Button variant="ghost" size="sm" data-testid={`cancel-invite-${inv.id}`} onClick={() => cancelInvite(inv.id)} className="text-[#8C532B] hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </RequestPanel>
      )}

      {canManage && (
        <RequestPanel title="Richieste di adesione alla scuderia" testid="join-requests-panel" empty="Nessuna richiesta di adesione.">
          {joinReqs.map((r) => (
            <JoinRequestRow key={r.id} req={r}
              onApprove={(role) => approveJoin(r, role)}
              onReject={() => act(`/stables/${current.id}/join-requests/${r.id}/reject`, "Richiesta rifiutata")} />
          ))}
        </RequestPanel>
      )}

      {canManage && (
        <RequestPanel title="Richieste di associazione a cavalli" testid="horse-requests-panel" empty="Nessuna richiesta di associazione.">
          {horseReqs.map((r) => (
            <div key={r.id} className="flex items-center justify-between py-2.5 gap-2" data-testid={`horse-req-${r.id}`}>
              <div>
                <p className="text-sm font-medium text-[#1B3B2B]">{r.user_name} → {r.horse_name}</p>
                <p className="text-xs text-[#5F6B64]">{r.user_email}{r.horse_microchip ? ` · chip ${r.horse_microchip}` : ""}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" data-testid={`approve-horse-req-${r.id}`} onClick={() => act(`/stables/${current.id}/horse-requests/${r.id}/approve`, "Cavallo associato")} className="bg-[#1B3B2B] hover:bg-[#142E1F]"><Check className="h-4 w-4 mr-1" /> Approva</Button>
                <Button size="sm" variant="outline" data-testid={`reject-horse-req-${r.id}`} onClick={() => act(`/stables/${current.id}/horse-requests/${r.id}/reject`, "Richiesta rifiutata")}>Rifiuta</Button>
              </div>
            </div>
          ))}
        </RequestPanel>
      )}

      {removeTarget && (
        <Dialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
          <DialogContent data-testid="remove-member-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Rimuovere dalla scuderia?</DialogTitle>
              <DialogDescription>
                {removeTarget.name} sarà rimosso da questa scuderia. L'account Equiro e tutti i dati storici NON vengono eliminati.
                {removeTarget.role === "owner" ? " Verranno rimosse solo le associazioni ai cavalli di questa scuderia." : ""}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRemoveTarget(null)} data-testid="remove-member-cancel">Annulla</Button>
              <Button onClick={removeMember} data-testid="remove-member-confirm" className="bg-red-600 hover:bg-red-700 text-white">Rimuovi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function JoinRequestRow({ req, onApprove, onReject }) {
  const [role, setRole] = useState(req.desired_role || "staff");
  return (
    <div className="flex items-center justify-between py-2.5 gap-2 flex-wrap" data-testid={`join-req-${req.id}`}>
      <div>
        <p className="text-sm font-medium text-[#1B3B2B]">{req.user_name}</p>
        <p className="text-xs text-[#5F6B64]">{req.user_email} · desidera: {ROLE_LABEL[req.desired_role]}</p>
      </div>
      <div className="flex items-center gap-1.5">
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-[150px] h-9" data-testid={`join-role-${req.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="staff">Staff</SelectItem>
            <SelectItem value="manager">Gestore</SelectItem>
            <SelectItem value="owner">Proprietario cavallo</SelectItem>
            <SelectItem value="readonly">Lettura</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" data-testid={`approve-join-${req.id}`} onClick={() => onApprove(role)} className="bg-[#1B3B2B] hover:bg-[#142E1F]"><Check className="h-4 w-4 mr-1" /> Approva</Button>
        <Button size="sm" variant="outline" data-testid={`reject-join-${req.id}`} onClick={onReject}>Rifiuta</Button>
      </div>
    </div>
  );
}


function RequestPanel({ title, testid, empty, children }) {
  const items = Array.isArray(children) ? children : (children ? [children] : []);
  const hasItems = items.some(Boolean);
  return (
    <div className="bg-white rounded-xl border border-[#E5DEC9] p-6" data-testid={testid}>
      <h3 className="font-display font-semibold text-[#1B3B2B] mb-2">{title}</h3>
      {hasItems ? <div className="divide-y divide-[#F0EAE1]">{children}</div>
        : <p className="text-sm text-[#5F6B64]">{empty}</p>}
    </div>
  );
}

function OwnerHorses({ member, current, horses, canManage, onSaved }) {
  const [owned, setOwned] = useState((member.owned_horses || []).map((h) => h.id));
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setOwned((member.owned_horses || []).map((h) => h.id)); }, [member]);

  const horseById = (id) => horses.find((h) => h.id === id) || (member.owned_horses || []).find((h) => h.id === id);
  const q = query.trim().toLowerCase();
  const suggestions = q
    ? horses.filter((h) =>
        !owned.includes(h.id) &&
        ((h.display_name || "").toLowerCase().includes(q) || (h.microchip || "").toLowerCase().includes(q)))
        .slice(0, 8)
    : [];

  const persist = async (nextOwned) => {
    setSaving(true);
    try {
      await api.put(`/stables/${current.id}/members/${member.user_id}/horses`, { horse_ids: nextOwned });
      setOwned(nextOwned);
      toast.success("Cavalli assegnati aggiornati");
      onSaved && onSaved();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addHorse = (id) => { setQuery(""); persist([...owned, id]); };
  const removeHorse = (id) => persist(owned.filter((x) => x !== id));

  return (
    <div className="mt-2 ml-1 pl-3 border-l-2 border-[#F0EAE1]" data-testid={`owner-horses-${member.user_id}`}>
      <p className="font-mono-label text-[10px] text-[#8C532B] mb-1.5">Cavalli assegnati</p>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {owned.length === 0 && <span className="text-xs text-[#5F6B64]">Nessun cavallo assegnato.</span>}
        {owned.map((id) => {
          const h = horseById(id);
          return (
            <span key={id} data-testid={`owned-chip-${id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#F0EAE1] text-[#1B3B2B] px-2.5 py-0.5 text-xs">
              {h ? h.display_name : id}
              {canManage && (
                <button disabled={saving} onClick={() => removeHorse(id)} data-testid={`remove-owned-${id}`} className="text-[#8C532B] hover:text-red-600 disabled:opacity-40">
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          );
        })}
      </div>
      {canManage && (
        <div className="relative max-w-sm">
          <div className="flex items-center gap-2 rounded-md border border-[#E5DEC9] px-2.5 py-1.5 bg-white">
            <Search className="h-3.5 w-3.5 text-[#8C532B]" />
            <input
              data-testid={`assign-search-${member.user_id}`}
              className="flex-1 text-sm outline-none bg-transparent"
              placeholder="Cerca per nome o microchip…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#8C532B]" />}
          </div>
          {suggestions.length > 0 && (
            <div className="absolute z-20 mt-1 w-full rounded-md border border-[#E5DEC9] bg-white shadow-lg overflow-hidden" data-testid={`assign-suggestions-${member.user_id}`}>
              {suggestions.map((h) => (
                <button
                  key={h.id}
                  data-testid={`assign-option-${h.id}`}
                  onClick={() => addHorse(h.id)}
                  className="flex w-full items-center justify-between px-3 py-2 text-sm text-left hover:bg-[#F0EAE1]"
                >
                  <span className="text-[#1B3B2B]">{h.display_name}</span>
                  {h.microchip && <span className="text-xs text-[#5F6B64]">{h.microchip}</span>}
                </button>
              ))}
            </div>
          )}
          {q && suggestions.length === 0 && (
            <p className="text-xs text-[#5F6B64] mt-1">Nessun cavallo trovato.</p>
          )}
        </div>
      )}
    </div>
  );
}


const TTYPES = ["farmaco", "integratore", "medicazione", "trattamento", "altro"];
function TreatmentsManager({ current, canManage }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("farmaco");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [dups, setDups] = useState([]);
  const [merge, setMerge] = useState(null);   // { source, target|null }
  const [mQ, setMQ] = useState("");
  const [merging, setMerging] = useState(false);

  const load = useCallback(() => {
    if (!current) return;
    setLoading(true);
    api.get(`/stables/${current.id}/treatments/manage`, { params: { q } })
      .then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, [current, q]);
  const loadDups = useCallback(() => {
    if (!current) return;
    api.get(`/stables/${current.id}/treatment-duplicates`).then((r) => setDups(r.data || [])).catch(() => setDups([]));
  }, [current]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { loadDups(); }, [loadDups]);

  const add = async () => {
    if (!name.trim()) return;
    try { await api.post(`/stables/${current.id}/treatments`, { name: name.trim(), type }); setName(""); load(); loadDups(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const saveRename = async (t) => {
    const nm = draft.trim(); if (!nm) return;
    try { await api.put(`/stables/${current.id}/treatments/${t.id}`, { name: nm }); toast.success("Trattamento rinominato"); setEditing(null); setDraft(""); load(); loadDups(); }
    catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "term_exists") { setEditing(null); setDraft(""); openMerge(t, { id: d.target_id, name: d.target_name }); return; }
      toast.error(errText(typeof d === "object" ? d?.message : d));
    }
  };
  const toggle = async (t) => {
    try { await api.put(`/stables/${current.id}/treatments/${t.id}`, { archived: !t.archived }); load(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Eliminare il trattamento «${t.name}»?`)) return;
    try { await api.delete(`/stables/${current.id}/treatments/${t.id}`); toast.success("Trattamento eliminato"); load(); loadDups(); }
    catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "referenced") { toast.error(d.message); return; }
      toast.error(errText(typeof d === "object" ? d?.message : d));
    }
  };
  const openMerge = (source, presetTarget = null) => { setMerge({ source, target: presetTarget }); setMQ(""); };
  const closeMerge = () => { setMerge(null); setMQ(""); };
  const doMerge = async () => {
    if (!merge?.target) return;
    setMerging(true);
    try {
      const { data } = await api.post(`/stables/${current.id}/treatments/${merge.source.id}/merge`, { target_id: merge.target.id });
      toast.success(`Uniti — ${data.reassigned} terapi${data.reassigned === 1 ? "a" : "e"} riassegnat${data.reassigned === 1 ? "a" : "e"}`);
      closeMerge(); load(); loadDups();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
    finally { setMerging(false); }
  };
  const nq = (mQ || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
  const mResults = merge && !merge.target
    ? items.filter((i) => i.id !== merge.source.id && !i.archived && ((i.name || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(nq) || (i.aliases || []).some((a) => a.includes(nq)))).slice(0, 20)
    : [];

  return (
    <div className="space-y-6" data-testid="treatments-manager">
      <div>
        <h3 className="font-display font-semibold text-[#1B3B2B] mb-1">Catalogo trattamenti</h3>
        <p className="text-xs text-[#5F6B64] mb-3">Farmaci e trattamenti della scuderia. Dose, via, frequenza e durata restano proprietà della singola terapia.</p>
        {canManage && (
          <div className="flex gap-2 mb-3">
            <Input data-testid="tm-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nuovo trattamento" onKeyDown={(e) => e.key === "Enter" && add()} />
            <Select value={type} onValueChange={setType}><SelectTrigger className="w-36" data-testid="tm-type"><SelectValue /></SelectTrigger><SelectContent>{TTYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            <Button data-testid="tm-add" onClick={add} className="bg-[#1B3B2B] hover:bg-[#142E1F]"><Plus className="h-4 w-4" /></Button>
          </div>
        )}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C6D4F]" />
          <Input data-testid="tm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca trattamento…" className="pl-9" />
        </div>

        {dups.length > 0 && (
          <div className="rounded-lg border border-[#E5C98A] bg-[#FBF6E8] p-3 mb-3" data-testid="tm-duplicates">
            <p className="text-xs font-semibold text-[#8C6A1F] mb-2">Possibili duplicati ({dups.length}) — suggerimenti, nessuna unione automatica</p>
            <div className="space-y-1.5">
              {dups.map((g, idx) => (
                <div key={idx} data-testid={`tm-dup-${idx}`} className="flex items-center justify-between gap-2 text-sm flex-wrap">
                  <span className="text-[#1B3B2B]"><strong>{g.a.name}</strong> ({g.a.usage_count}) · <strong>{g.b.name}</strong> ({g.b.usage_count}) <span className="text-[11px] text-[#8C6A1F]">— {g.reason}</span></span>
                  {canManage && <button data-testid={`tm-dup-merge-${idx}`} onClick={() => openMerge({ id: g.a.id, name: g.a.name, usage_count: g.a.usage_count }, { id: g.b.id, name: g.b.name })} className="text-xs text-[#8C532B] hover:underline shrink-0">Unisci…</button>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-lg border border-[#E5DEC9] divide-y divide-[#F0EAE1] bg-white">
          {loading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#8C6D4F]" /></div>
            : items.length === 0 ? <p className="text-sm text-[#5F6B64] px-4 py-6">Nessun trattamento.</p>
            : items.map((t) => (
              <div key={t.id} data-testid={`tm-row-${t.id}`} className={`flex items-center gap-2 px-3 py-2.5 flex-wrap ${t.archived ? "opacity-60" : ""}`}>
                {editing === t.id ? (
                  <>
                    <Input data-testid={`tm-rename-input-${t.id}`} value={draft} onChange={(e) => setDraft(e.target.value)} className="h-8 flex-1 min-w-[140px]" onKeyDown={(e) => e.key === "Enter" && saveRename(t)} />
                    <Button size="sm" data-testid={`tm-rename-save-${t.id}`} onClick={() => saveRename(t)} className="bg-[#1B3B2B] hover:bg-[#142E1F] h-8">Salva</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(null); setDraft(""); }} className="h-8">Annulla</Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 min-w-0 truncate text-sm text-[#1B3B2B] font-medium">{t.name}<span className="text-[11px] text-[#8C6D4F] font-normal"> · {t.type}</span>{t.archived && <span className="text-[10px] text-[#B26A00]"> · archiviato</span>}</span>
                    {(t.aliases || []).length > 0 && <span className="text-[10px] text-[#8C6D4F] shrink-0" data-testid={`tm-aliases-${t.id}`}>alias: {(t.aliases || []).join(", ")}</span>}
                    <span className="text-xs text-[#5F6B64] w-14 text-right shrink-0">{t.usage_count} usi</span>
                    {canManage && <>
                      <button data-testid={`tm-merge-${t.id}`} onClick={() => openMerge({ id: t.id, name: t.name, usage_count: t.usage_count })} className="text-xs text-[#8C532B] hover:underline shrink-0">Unisci</button>
                      <button data-testid={`tm-rename-${t.id}`} onClick={() => { setEditing(t.id); setDraft(t.name); }} className="text-xs text-[#1B3B2B] hover:underline shrink-0">Rinomina</button>
                      <button data-testid={`tm-toggle-${t.id}`} onClick={() => toggle(t)} className="text-xs text-[#8C6D4F] hover:underline shrink-0">{t.archived ? "Ripristina" : "Archivia"}</button>
                      <button data-testid={`tm-delete-${t.id}`} onClick={() => remove(t)} className="text-xs text-red-600 hover:underline shrink-0">Elimina</button>
                    </>}
                  </>
                )}
              </div>
            ))}
        </div>
      </div>

      <Dialog open={!!merge} onOpenChange={(o) => { if (!o) closeMerge(); }}>
        <DialogContent data-testid="tm-merge-dialog">
          <DialogHeader><DialogTitle className="font-display">Unisci trattamenti</DialogTitle>
            <DialogDescription className="text-xs text-[#5F6B64]">Scegli il trattamento canonicale di destinazione. Le terapie collegate vengono riassegnate; lo storico e i nomi originali sono conservati (il vecchio nome diventa alias di ricerca).</DialogDescription></DialogHeader>
          {merge && (
            <div className="space-y-3">
              <p className="text-sm"><span className="text-[#5F6B64]">Sorgente: </span><strong>{merge.source.name}</strong>{merge.source.usage_count != null ? <span className="text-[#5F6B64]"> · {merge.source.usage_count} usi</span> : null}</p>
              {!merge.target ? (
                <div>
                  <Label className="text-xs">Destinazione (canonicale)</Label>
                  <Input data-testid="tm-merge-target-search" value={mQ} onChange={(e) => setMQ(e.target.value)} autoFocus placeholder="Cerca destinazione…" className="mt-1" />
                  <div className="mt-2 max-h-52 overflow-auto rounded-lg border border-[#E5DEC9] divide-y divide-[#F0EAE1]">
                    {mResults.length === 0 ? <p className="text-xs text-[#8C6D4F] px-3 py-3">Nessun trattamento.</p>
                      : mResults.map((r) => (
                        <button key={r.id} data-testid={`tm-merge-target-${r.id}`} onClick={() => setMerge((m) => ({ ...m, target: r }))} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-[#F0EAE1]">
                          <span className="truncate text-[#1B3B2B]">{r.name}</span><span className="text-xs text-[#8C6D4F] shrink-0">{r.usage_count} usi</span>
                        </button>
                      ))}
                  </div>
                </div>
              ) : (
                <div data-testid="tm-merge-preview" className="rounded-lg border border-[#BFD9C4] bg-[#F1F8F2] p-3 space-y-2 text-center">
                  <p className="text-base font-semibold text-[#1B3B2B]">«{merge.source.name}» → «{merge.target.name}»</p>
                  <p className="text-xs text-[#5F6B64]">Le terapie di «{merge.source.name}» saranno riassegnate a «{merge.target.name}». «{merge.source.name}» resterà come <strong>alias</strong>.</p>
                  <button data-testid="tm-merge-change" onClick={() => setMerge((m) => ({ ...m, target: null }))} className="text-xs text-[#8C532B] hover:underline">Cambia destinazione</button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeMerge}>Annulla</Button>
            <Button data-testid="tm-merge-confirm" onClick={doMerge} disabled={!merge?.target || merging} className="bg-[#1B3B2B] hover:bg-[#142E1F]">{merging && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Conferma unione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
